import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import { setLocale as setI18nLocale, DEFAULT_LOCALE, type SupportedLocale } from '@/i18n';

export type ColorScheme = 'light' | 'dark' | 'system';

const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

export const useAppStore = defineStore(
  'app',
  () => {
    const colorScheme = ref<ColorScheme>('system');
    const isLoading = ref(false);
    const locale = ref<SupportedLocale>(DEFAULT_LOCALE);

    const isDark = computed(() => {
      if (colorScheme.value === 'system') {
        return mediaQuery.matches;
      }

      return colorScheme.value === 'dark';
    });

    function setColorScheme(scheme: ColorScheme): void {
      colorScheme.value = scheme;
      updateDocumentTheme();
    }

    function updateDocumentTheme(): void {
      if (isDark.value) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    function onSystemThemeChange(): void {
      if (colorScheme.value === 'system') {
        updateDocumentTheme();
      }
    }

    mediaQuery.addEventListener('change', onSystemThemeChange);

    function setLoading(value: boolean): void {
      isLoading.value = value;
    }

    function setLocale(newLocale: SupportedLocale): void {
      locale.value = newLocale;
      setI18nLocale(newLocale);
    }

    function initializeLocale(): void {
      setI18nLocale(locale.value);
    }

    return {
      colorScheme,
      isDark,
      isLoading,
      locale,
      setColorScheme,
      setLoading,
      setLocale,
      initializeLocale,
      updateDocumentTheme,
    };
  },
  {
    persist: {
      paths: ['colorScheme', 'locale'],
    },
  },
);
